/**
 * クライアントサイド遷移が「コミットされないまま固まる」ことからの脱出（PRODUCT_DEV-612）。
 *
 * ## 何が起きるか
 *
 * React Router v7 のナビゲーションは history を先に push してから、React 側の location
 * 更新を `startTransition` で行う。**遷移先がサスペンドしたままだと transition が
 * コミットされない**ため、
 *
 * - アドレスバーの URL は遷移先に変わっている
 * - 画面は前のまま（`<Suspense>` の fallback にも切り替わらない。transition 中は
 *   既存 UI を保つのが React の仕様）
 * - ボタンを押しても何も起きない（handler は動くが、やはりコミットされない）
 *
 * という「操作不能だがエラーも出ない」状態になる。ユーザから見ると完全なフリーズで、
 * リロードするまで復帰できない。
 *
 * 実際に確認された発生源は Service Worker の precache 汚染によるルートチャンクの
 * 読み込み失敗（詳細は `@/lib/chunkRecovery`）だが、**サスペンドが解けない理由は
 * 一つとは限らない**。原因が何であれ、ユーザを操作不能のまま放置しないための最後の網として
 * ここでウォッチドッグを持つ。
 *
 * ## やること
 *
 * 「ブラウザの URL」と「React がコミット済みの location」がズレたまま一定時間戻らなければ、
 * 異常とみなして**ハードナビゲーション（フルページロード）で遷移先へ抜ける**。
 * あわせて原因調査用の情報をコンソールと RUM に残す。
 *
 * ## 誤検知しない理由
 *
 * - loader 実行中は URL がまだ変わらない（React Router は loader 解決後に push する）
 * - `useBlocker` 系でブロックされた遷移も URL が変わらない
 * - hash のみ / state のみの遷移は pathname+search が変わらない
 * - popstate（戻る/進む）で固まった場合も救済対象にしたいので、あえて区別しない
 */
import {
  getBreadcrumbs,
  getPendingChunks,
  getRouterState,
  safeStringify,
} from "@/lib/navDiagnostics";
import { recordRumError } from "@/lib/rum";

/** この時間ズレたままならフリーズとみなす。遅い回線でのチャンク取得を殺さない程度に長くとる。 */
export const NAV_STUCK_TIMEOUT_MS = 8_000;

/** ズレの監視間隔。 */
export const NAV_POLL_INTERVAL_MS = 1_000;

/** ハードナビゲーションの記録キー（sessionStorage）。 */
export const NAV_RECOVER_KEY = "nav-stuck-recover-at";

/** この時間内の再発ではハードナビゲーションしない（リロードループ防止）。 */
export const NAV_RECOVER_COOLDOWN_MS = 30_000;

/** 直前のレポートの退避キー（sessionStorage）。復帰後のページで読み直して再掲する。 */
export const NAV_REPORT_KEY = "nav-stuck-report";

export interface NavigationCheck {
  /** React がコミット済みの location（pathname + search）。 */
  committed: string;
  /** ブラウザの現在 URL（pathname + search）。 */
  current: string;
  /** ズレを最初に観測した時刻。未観測なら null。 */
  stuckSince: number | null;
  now: number;
  timeoutMs?: number;
}

export interface NavigationVerdict {
  stuckSince: number | null;
  shouldRecover: boolean;
}

/**
 * ズレの状態を1ステップ進める純関数。
 *
 * ズレが解消したら計測をリセットし、閾値を超え続けていたら復帰を指示する。
 */
export function evaluateNavigation({
  committed,
  current,
  stuckSince,
  now,
  timeoutMs = NAV_STUCK_TIMEOUT_MS,
}: NavigationCheck): NavigationVerdict {
  if (committed === current) return { stuckSince: null, shouldRecover: false };
  if (stuckSince === null) return { stuckSince: now, shouldRecover: false };
  return { stuckSince, shouldRecover: now - stuckSince >= timeoutMs };
}

function readRecoveredAt(): number {
  try {
    return Number(sessionStorage.getItem(NAV_RECOVER_KEY) ?? 0);
  } catch {
    return 0;
  }
}

function writeRecoveredAt(now: number): void {
  try {
    sessionStorage.setItem(NAV_RECOVER_KEY, String(now));
  } catch {
    // 記録できなくても復帰は試す（次回クールダウンが効かないだけ）
  }
}

function writeStuckReport(json: string): void {
  try {
    sessionStorage.setItem(NAV_REPORT_KEY, json);
  } catch {
    // 退避できなくても Console には出ているので復帰は続ける
  }
}

/**
 * 直前のフルページロードで退避したレポートを取り出して消す。
 *
 * ウォッチドッグは復帰のためにフルページロードするので、発火時の Console 出力は
 * 「Preserve log」を事前に ON にしていない限り流れてしまう。復帰後のページで
 * これを読み直して再掲することで、**設定に関係なく必ず 1 本残る**ようにする。
 */
export function readAndClearStuckReport(): string | null {
  try {
    const json = sessionStorage.getItem(NAV_REPORT_KEY);
    if (json) sessionStorage.removeItem(NAV_REPORT_KEY);
    return json;
  } catch {
    return null;
  }
}

/**
 * 固まった遷移からの脱出。
 *
 * 原因が特定できるよう、コンソールと RUM に状況（遷移元/先・停止時間・SW の有無・
 * Cache Storage のキー）を残してからハードナビゲーションする。
 * クールダウン中は記録だけ行い、ハードナビゲーションはしない（ループ防止）。
 */
export async function recoverFromStuckNavigation(detail: {
  from: string;
  to: string;
  stuckMs: number;
}): Promise<void> {
  let cacheKeys: string[] = [];
  try {
    if ("caches" in window) cacheKeys = await caches.keys();
  } catch {
    // 取得できなくても報告は続ける
  }

  const now = Date.now();
  const recoveredAt = readRecoveredAt();
  const withinCooldown = now - recoveredAt < NAV_RECOVER_COOLDOWN_MS;
  const report = {
    ...detail,
    // ここが空でなければ原因は lazy チャンクの読み込み。空なら遷移先のサスペンド
    // 以外を疑う。自由記録と振り返りの差分を切り分ける決め手になる。
    pendingChunks: getPendingChunks(),
    // react-router 自身の状態。`from`（React がコミット済み）と突き合わせて読む:
    // - navigationState が "loading" → react-router がまだ解決していない（loader/lazy 側）
    // - "idle" かつ routerLocation が `to` と一致 → react-router は完了しており、
    //   React 側が transition をコミットしていない（React 側の問題）
    routerState: getRouterState(),
    breadcrumbs: getBreadcrumbs(),
    swScriptUrl: navigator.serviceWorker?.controller?.scriptURL ?? null,
    cacheKeys,
    withinCooldown,
  };

  // オブジェクトではなく文字列で出す。この直後にフルページロードするため、
  // オブジェクトを渡すと Chrome 上で参照が死んで展開できなくなる（"No properties"）。
  const json = safeStringify(report);
  console.error(
    "[nav] client-side navigation did not commit; falling back to a full page load",
    json,
  );
  // 復帰後のページで再掲するために退避する（「Preserve log」が OFF でも残るように）。
  writeStuckReport(json);
  recordRumError(new Error("stuck-navigation"), report);

  if (withinCooldown) return;
  writeRecoveredAt(now);
  window.location.replace(window.location.href);
}
